import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import {
  BlockScheduleAdminService,
  BsIncoterm,
  BsIntegrator,
  BsInverterModel,
  BsProjectStatus,
  BsTransformer
} from '../../services/blockschedule-admin.service';
import { BlockscheduleReportComponent } from '../blockschedule-report/blockschedule-report';

type BsTab = 'reports' | 'inverterModel' | 'incoTerms' | 'projectStatus' | 'integrator' | 'transformer';

@Component({
  selector: 'app-blockschedule-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ToastModule, BlockscheduleReportComponent],
  providers: [MessageService],
  templateUrl: './blockschedule-admin.html',
  styleUrl: './blockschedule-admin.css',
})
export class BlockscheduleAdmin implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  activeTab: BsTab = 'reports';

  inverterList: BsInverterModel[] = [];
  incoTermsList: BsIncoterm[] = [];
  projectStatusList: BsProjectStatus[] = [];
  integratorList: BsIntegrator[] = [];
  transformerList: BsTransformer[] = [];

  loading = false;
  saving = false;
  deleting = false;

  showFormDialog = false;
  isEditing = false;
  formName = '';
  formDescription = '';

  showDeleteDialog = false;
  deleteTargetId = 0;
  deleteTargetName = '';

  constructor(
    private adminService: BlockScheduleAdminService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadCurrentTab();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setTab(tab: BsTab): void {
    this.activeTab = tab;
    this.loadCurrentTab();
  }

  get currentList(): any[] {
    if (this.activeTab === 'inverterModel') return this.inverterList;
    if (this.activeTab === 'incoTerms') return this.incoTermsList;
    if (this.activeTab === 'projectStatus') return this.projectStatusList;
    if (this.activeTab === 'integrator') return this.integratorList;
    return this.transformerList;
  }

  get hasDescription(): boolean {
    return this.activeTab === 'inverterModel' || this.activeTab === 'transformer';
  }

  get dialogTitle(): string {
    const action = this.isEditing ? 'Edit' : 'Add';
    const labels: Record<BsTab, string> = {
      reports: 'Reports',
      inverterModel: 'Inverter Model',
      incoTerms: 'Inco Term',
      projectStatus: 'Project Status',
      integrator: 'Integrator',
      transformer: 'Transformer'
    };
    return `${action} ${labels[this.activeTab]}`;
  }

  openAddDialog(): void {
    this.isEditing = false;
    this.formName = '';
    this.formDescription = '';
    this.showFormDialog = true;
  }

  openEditDialog(item: any): void {
    this.isEditing = true;
    this.formName = item.name ?? '';
    this.formDescription = item.description ?? '';
    this.activeEditId = this.getItemId(item);
    this.showFormDialog = true;
  }

  activeEditId = 0;

  saveForm(): void {
    if (!this.formName.trim()) {
      this.messageService.add({ severity: 'error', summary: 'Validation', detail: 'Name is required.' });
      return;
    }

    this.saving = true;
    const id = this.isEditing ? this.activeEditId : 0;

    const obs$ = this.buildSaveObservable(id);
    obs$.pipe(finalize(() => (this.saving = false)), takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.showFormDialog = false;
        this.messageService.add({
          severity: 'success',
          summary: this.isEditing ? 'Updated' : 'Added',
          detail: `${this.dialogTitle.replace('Add ', '').replace('Edit ', '')} ${this.isEditing ? 'updated' : 'added'} successfully.`
        });
        this.loadCurrentTab();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save. Please try again.' });
      }
    });
  }

  confirmDelete(item: any): void {
    this.deleteTargetId = this.getItemId(item);
    this.deleteTargetName = item.name ?? '';
    this.showDeleteDialog = true;
  }

  executeDelete(): void {
    this.deleting = true;
    const obs$ = this.buildDeleteObservable(this.deleteTargetId);
    obs$.pipe(finalize(() => (this.deleting = false)), takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.showDeleteDialog = false;
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Record deleted successfully.' });
        this.loadCurrentTab();
      },
      error: () => {
        this.showDeleteDialog = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Delete failed. The record may be in use.' });
      }
    });
  }

  private loadCurrentTab(): void {
    if (this.activeTab === 'reports') return;
    this.loading = true;
    const obs$ = this.buildLoadObservable();
    obs$.pipe(finalize(() => (this.loading = false)), takeUntil(this.destroy$)).subscribe({
      next: (data: any[]) => this.assignList(data),
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load data.' });
      }
    });
  }

  private buildLoadObservable(): Observable<any[]> {
    if (this.activeTab === 'inverterModel') return this.adminService.getInverterModels();
    if (this.activeTab === 'incoTerms') return this.adminService.getIncoterms();
    if (this.activeTab === 'projectStatus') return this.adminService.getProjectStatuses();
    if (this.activeTab === 'integrator') return this.adminService.getIntegrators();
    return this.adminService.getTransformers();
  }

  private assignList(data: any[]): void {
    const sorted = [...(data ?? [])].sort((a, b) => this.getItemId(a) - this.getItemId(b));
    if (this.activeTab === 'inverterModel') this.inverterList = sorted;
    else if (this.activeTab === 'incoTerms') this.incoTermsList = sorted;
    else if (this.activeTab === 'projectStatus') this.projectStatusList = sorted;
    else if (this.activeTab === 'integrator') this.integratorList = sorted;
    else this.transformerList = sorted;
  }

  private buildSaveObservable(id: number): Observable<boolean> {
    if (this.activeTab === 'inverterModel')
      return this.adminService.saveInverterModel({ inverterModelId: id, name: this.formName.trim(), description: this.formDescription.trim() || undefined });
    if (this.activeTab === 'incoTerms')
      return this.adminService.saveIncoterm({ incotermId: id, name: this.formName.trim() });
    if (this.activeTab === 'projectStatus')
      return this.adminService.saveProjectStatus({ projectStatusId: id, name: this.formName.trim() });
    if (this.activeTab === 'integrator')
      return this.adminService.saveIntegrator({ integratorId: id, name: this.formName.trim() });
    return this.adminService.saveTransformer({ transformerId: id, name: this.formName.trim(), description: this.formDescription.trim() || undefined });
  }

  private buildDeleteObservable(id: number): Observable<boolean> {
    if (this.activeTab === 'inverterModel') return this.adminService.deleteInverterModel(id);
    if (this.activeTab === 'incoTerms') return this.adminService.deleteIncoterm(id);
    if (this.activeTab === 'projectStatus') return this.adminService.deleteProjectStatus(id);
    if (this.activeTab === 'integrator') return this.adminService.deleteIntegrator(id);
    return this.adminService.deleteTransformer(id);
  }

  getItemId(item: any): number {
    if (this.activeTab === 'inverterModel') return item.inverterModelId;
    if (this.activeTab === 'incoTerms') return item.incotermId;
    if (this.activeTab === 'projectStatus') return item.projectStatusId;
    if (this.activeTab === 'integrator') return item.integratorId;
    return item.transformerId;
  }
}
